import React, { useContext, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AppContext } from '../../../../context/AppContextProvider';
import { IPaginationParams } from '../../../../services/types';
import {
  ISportButtonEventFilterParams,
  ISportButtonEventProps,
  SportButtonEventStatus,
} from '../../../../services/Sports/ButtonEvents/types';
import {
  useGetAllSportButtonEventsQuery,
  useGetSportButtonEventsCountQuery,
} from '../../../../services/Sports/ButtonEvents/hooks/useQuery';
import {
  useResendSportButtonEventAsync,
  useResendSportButtonEventsBulkAsync,
} from '../../../../services/Sports/ButtonEvents/hooks/useMutation';
import { useGetAllDevicesByLicenseAsync } from '../../../../services/Devices/hooks/useMutation';
import { useJobQuery } from '../../../../services/Jobs/hooks/useQuery';

const RESEND_JOB_SUCCESS_LINGER_MS = 5000;

export type QuickFilter = 'hoje' | '7d' | 'todos' | 'personalizado';

const QUICK_FILTER_LABELS: Record<QuickFilter, string> = {
  hoje: 'Hoje',
  '7d': 'Últimos 7 dias',
  todos: 'Todo o Período',
  personalizado: 'Personalizado',
};

export { QUICK_FILTER_LABELS };

export const useButtonEvents = () => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const [pagination, setPagination] = useState<IPaginationParams>({ pageNumber: 1, pageSize: 25 });
  const [deviceId, setDeviceId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<SportButtonEventStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilter>('todos');
  const [filter, setFilter] = useState<ISportButtonEventFilterParams>({});
  const [selected, setSelected] = useState<number[]>([]);

  const [devices, setDevices] = useState<{ id: number; name: string }[]>([]);
  const { mutateAsync: fetchDevices } = useGetAllDevicesByLicenseAsync();

  useEffect(() => {
    fetchDevices({}).then((data) => {
      setDevices((data || []).map((d: any) => ({ id: d.id, name: d.name })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: events = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetAllSportButtonEventsQuery(pagination, filter);

  const { data: totalCount = 0, refetch: refetchCount } = useGetSportButtonEventsCountQuery(filter);

  // Contagens por status dentro do filtro atual (período/dispositivo/busca), pros cards de resumo.
  const { data: successCount = 0, refetch: refetchSuccessCount } =
    useGetSportButtonEventsCountQuery({ ...filter, status: 'Success' });
  const { data: sendFailedCount = 0, refetch: refetchSendFailedCount } =
    useGetSportButtonEventsCountQuery({ ...filter, status: 'SendFailed' });
  const { data: deviceNotFoundCount = 0, refetch: refetchDeviceNotFoundCount } =
    useGetSportButtonEventsCountQuery({ ...filter, status: 'DeviceNotFound' });
  const { data: invalidPayloadCount = 0, refetch: refetchInvalidPayloadCount } =
    useGetSportButtonEventsCountQuery({ ...filter, status: 'InvalidPayload' });

  const { mutateAsync: resendOne, isLoading: isResendingOne } = useResendSportButtonEventAsync();
  const { mutateAsync: resendBulk, isLoading: isResendingBulk } = useResendSportButtonEventsBulkAsync();

  // Job de reenvio em lote (roda no backend em lotes de 10 a cada 2min). Enquanto isToastOpen,
  // o toast de progresso fica visível; refetchedForJobRef evita disparar handleRefresh() a cada
  // tick de polling depois que o job já terminou.
  const [resendJobId, setResendJobId] = useState<number | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
  const refetchedForJobRef = React.useRef<number | null>(null);
  const { data: resendJob } = useJobQuery(resendJobId);

  useEffect(() => {
    if (!resendJob) return;
    if (resendJob.status !== 'Completed' && resendJob.status !== 'Failed') return;
    if (refetchedForJobRef.current === resendJobId) return;

    refetchedForJobRef.current = resendJobId;
    handleRefresh();

    const timer = setTimeout(() => {
      setIsToastOpen(false);
      setResendJobId(null);
    }, RESEND_JOB_SUCCESS_LINGER_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendJob?.status, resendJobId]);

  const applyFilters = (start: Dayjs | null = startDate, end: Dayjs | null = endDate) => {
    setFilter({
      deviceId,
      status,
      search: search || undefined,
      from: start ? start.startOf('day').toISOString() : undefined,
      to: end ? end.endOf('day').toISOString() : undefined,
    });
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    setSelected([]);
  };

  const handleQuickDateFilter = (preset: QuickFilter) => {
    setActiveQuickFilter(preset);
    let newStart: Dayjs | null = null;
    let newEnd: Dayjs | null = null;

    if (preset === 'hoje') {
      newStart = dayjs().startOf('day');
      newEnd = dayjs().endOf('day');
    } else if (preset === '7d') {
      newStart = dayjs().subtract(7, 'day').startOf('day');
      newEnd = dayjs().endOf('day');
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    setFilter((prev) => ({
      ...prev,
      from: newStart ? newStart.toISOString() : undefined,
      to: newEnd ? newEnd.toISOString() : undefined,
    }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    setSelected([]);
  };

  const handleClearFilters = () => {
    setDeviceId(undefined);
    setStatus(undefined);
    setSearch('');
    setStartDate(null);
    setEndDate(null);
    setActiveQuickFilter('todos');
    setFilter({});
    setPagination({ pageNumber: 1, pageSize: pagination.pageSize });
    setSelected([]);
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
    refetchSuccessCount();
    refetchSendFailedCount();
    refetchDeviceNotFoundCount();
    refetchInvalidPayloadCount();
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setSelected([]);
    setPagination((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected([]);
    setPagination({ pageNumber: 1, pageSize: parseInt(event.target.value, 10) });
  };

  const toggleSelected = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selected.length === events.length && events.length > 0) {
      setSelected([]);
    } else {
      setSelected(events.map((e: ISportButtonEventProps) => e.id));
    }
  };

  const handleResendOne = async (id: number) => {
    setIsShowLoadingComponent(true);
    try {
      await resendOne(id);
      showSnackbar('Evento reenviado com sucesso!', 'success');
      handleRefresh();
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao reenviar evento.', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleResendSelected = async () => {
    if (selected.length === 0) return;
    setIsShowLoadingComponent(true);
    try {
      const job = await resendBulk(selected);
      refetchedForJobRef.current = null;
      setResendJobId(job.jobId);
      setIsToastOpen(true);
      setSelected([]);
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao iniciar reenvio em lote.', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const closeResendToast = () => {
    setIsToastOpen(false);
    setResendJobId(null);
  };

  // "Olho" no toast: minimiza o toast e abre o dialog com o progresso em detalhe (mesmo padrão do
  // FaceSyncRunner). Fechar/minimizar o dialog faz o caminho inverso.
  const openResendDialog = () => {
    setIsToastOpen(false);
    setIsResendDialogOpen(true);
  };

  const minimizeResendDialog = () => {
    setIsResendDialogOpen(false);
    setIsToastOpen(true);
  };

  const closeResendDialog = () => {
    setIsResendDialogOpen(false);
    setResendJobId(null);
  };

  return {
    state: {
      events,
      totalCount,
      isLoading,
      isFetching,
      isResending: isResendingOne || isResendingBulk,
      pagination,
      devices,
      selected,
      filters: { deviceId, status, search, startDate, endDate, activeQuickFilter },
      statusCounts: {
        success: successCount,
        sendFailed: sendFailedCount,
        deviceNotFound: deviceNotFoundCount,
        invalidPayload: invalidPayloadCount,
      },
      resendJob,
      isResendToastOpen: isToastOpen,
      isResendDialogOpen,
    },
    actions: {
      setDeviceId,
      setStatus,
      setSearch,
      setStartDate,
      setEndDate,
      setActiveQuickFilter,
      applyFilters,
      handleQuickDateFilter,
      handleClearFilters,
      handleRefresh,
      setPagination,
      handleChangePage,
      handleChangeRowsPerPage,
      toggleSelected,
      toggleSelectAll,
      handleResendOne,
      handleResendSelected,
      openResendDialog,
      minimizeResendDialog,
      closeResendDialog,
      closeResendToast,
    },
  };
};
